import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';

interface ChannelOption {
  id: number;
  name: string;
  logoSquareUrl: string | null;
}

interface ChannelSearchDropdownProps {
  isDebouncing: boolean;
  isFetching: boolean;
  hasSearchQuery: boolean;
  options: ChannelOption[];
  selectedChannelId?: number;
  onSelect: (id: number, name: string) => void;
  listboxId: string;
}

function DropdownMessageCenter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-8 items-center justify-center px-2">
      <Typography
        variant="body1"
        className="!text-fx-neutral-600 w-full text-center leading-none"
      >
        {children}
      </Typography>
    </div>
  );
}

export function ChannelSearchDropdown({
  isDebouncing,
  isFetching,
  hasSearchQuery,
  options,
  selectedChannelId,
  onSelect,
  listboxId,
}: ChannelSearchDropdownProps) {
  if (!hasSearchQuery) {
    return (
      <DropdownMessageCenter>
        <FormattedMessage
          defaultMessage="Type to get options"
          description="Add contract channel dropdown: prompt before user searches"
        />
      </DropdownMessageCenter>
    );
  }

  if (isFetching || isDebouncing) {
    return (
      <DropdownMessageCenter>
        <FormattedMessage
          defaultMessage="Loading options, please wait"
          description="Add contract channel dropdown: shown while search is debouncing or API is in flight"
        />
      </DropdownMessageCenter>
    );
  }

  if (options.length === 0) {
    return (
      <DropdownMessageCenter>
        <FormattedMessage
          defaultMessage="No options found!"
          description="Add contract channel dropdown: API returned no matching channel"
        />
      </DropdownMessageCenter>
    );
  }

  return (
    <ul id={listboxId} className="flex flex-col">
      {options.map(option => {
        const isSelected = selectedChannelId === option.id;
        return (
          <li key={option.id}>
            <button
              type="button"
              className={clsx(
                'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left transition-colors',
                'hover:bg-fx-neutral-100 focus-visible:bg-fx-neutral-100 focus-visible:outline-none',
                isSelected && 'bg-fx-brand-50',
              )}
              onMouseDown={e => e.preventDefault()}
              onClick={() => onSelect(option.id, option.name)}
            >
              <ChannelIcon
                channelName={option.name}
                logoSquareUrl={option.logoSquareUrl}
              />
              <Typography variant="body1" className="truncate">
                {option.name}
              </Typography>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
